import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Play, AlertTriangle, Lock, Unlock, Star, Trash2, Copy, ChevronUp, ChevronDown } from 'lucide-react';
import { AppLayout } from './AppLayout';
import { EvaluationApiService } from '../services/evaluationApi';
import { ApiService } from '../services/apiService';
import { EvaluationTest } from '../types/evaluation';
import { AgentExecutionFlow, ExecutionEvent } from '../types';
import { ProviderResultColumn } from './ProviderResultColumn';
import { SimplifiedProviderColumn } from './SimplifiedProviderColumn';
import { ExecutionLimitWarningPopup } from './ExecutionLimitWarningPopup';
import { useSignalR } from '../contexts/useSignalR';
import { usePromptCache } from '../hooks/usePromptCache';
import { useViewMode } from '../hooks/useViewMode';

interface EvaluationExecutionInterfaceProps {
  testId?: string; // Optional prop, can also come from route params
}

export const EvaluationExecutionInterface: React.FC<EvaluationExecutionInterfaceProps> = ({ 
  testId: propTestId 
}) => {
  const { testId: routeTestId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const testId = propTestId || routeTestId;

  const [test, setTest] = useState<EvaluationTest | null>(null);
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [executionFlows, setExecutionFlows] = useState<Record<string, AgentExecutionFlow>>({});
  const [executionEvents, setExecutionEvents] = useState<Record<string, ExecutionEvent[]>>({});
  const [currentExecution, setCurrentExecution] = useState<string | null>(null);
  const [scoredExecutionCount, setScoredExecutionCount] = useState(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isPromptPanelCollapsed, setIsPromptPanelCollapsed] = useState(false);

  const evaluationApi = EvaluationApiService.getInstance();
  const apiService = ApiService.getInstance();
  const signalR = useSignalR();
  const { cachedPrompts, addToCache, removeFromCache } = usePromptCache();
  const { viewMode } = useViewMode();

  // Join user SignalR group on mount
  useEffect(() => {
    const joinUserSignalRGroup = async () => {
      try {
        const userIdentity = await apiService.getUserIdentity();
        if (userIdentity.email) {
          await signalR.joinUserGroup(userIdentity.email);
          console.log('Joined user SignalR group:', userIdentity.email);
        }
      } catch (error) {
        console.error('Failed to join user SignalR group:', error);
      }
    };

    joinUserSignalRGroup();
  }, [signalR, apiService]);

  // Load test configuration and resolve system prompt hashes
  useEffect(() => {
    if (!testId) {
      setError('No test ID provided');
      setIsLoading(false);
      return;
    }

    const loadTest = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Load test configuration
        const testData = await evaluationApi.getEvaluationTest(testId);
        if (!testData) {
          setError('Evaluation test not found');
          setIsLoading(false);
          return;
        }

        // Check if test is deleted
        if (testData.isDeleted) {
          setError('This evaluation test has been deleted and cannot be executed');
          setIsLoading(false);
          return;
        }

        setTest(testData);

        // Set fixed prompt if in Fixed mode
        if (testData.mode === 'Fixed' && testData.fixedPrompt) {
          setPrompt(testData.fixedPrompt);
        }

        // Load execution count if there's a limit
        if (testData.executionLimit) {
          try {
            const executions = await evaluationApi.listExecutions(testData.id);
            setScoredExecutionCount(executions.length);
          } catch (err) {
            console.error('Failed to load execution count:', err);
          }
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Failed to load evaluation test:', err);
        setError(err instanceof Error ? err.message : 'Failed to load evaluation test');
        setIsLoading(false);
      }
    };

    loadTest();
  }, [testId]);

  // Check execution limit before executing
  const handleExecuteClick = async () => {
    if (!test || !prompt.trim()) return;

    // Check execution limit and show warning if reached or exceeded
    if (test.executionLimit) {
      try {
        const executions = await evaluationApi.listExecutions(test.id);
        const totalCount = executions.length;
        setScoredExecutionCount(totalCount);

        // Show warning if limit reached or exceeded
        if (totalCount >= test.executionLimit) {
          setShowLimitWarning(true);
          return;
        }
      } catch (err) {
        console.error('Failed to check execution count:', err);
        // Continue with execution even if check fails
      }
    }

    await executeTest();
  };

  // Handle SignalR execution events - filter by execution ID since we receive all user events
  const handleExecutionEvent = useCallback((event: any) => {
    if (!test) return;

    // Extract base execution ID from the event's execution ID (format: baseId_providerId)
    const eventExecutionId = event.executionId;
    if (!eventExecutionId) return;

    // Check if this event belongs to one of our test's providers
    const isRelevantExecution = test.providerConfigs.some(config => 
      eventExecutionId.endsWith(`_${config.id}`)
    );

    if (!isRelevantExecution) return;

    // If this is the first event, extract and set the base execution ID
    if (!currentExecution && eventExecutionId.includes('_')) {
      const baseExecutionId = eventExecutionId.substring(0, eventExecutionId.lastIndexOf('_'));
      setCurrentExecution(baseExecutionId);
    }

    // Accumulate raw events per execution ID for clarification detection
    setExecutionEvents(prev => ({
      ...prev,
      [eventExecutionId]: [...(prev[eventExecutionId] || []), event]
    }));

    setExecutionFlows(prev => {
      // Get existing flow or create new one
      const existingFlow = prev[eventExecutionId] || {
        executionId: eventExecutionId,
        startTime: event.timestamp,
        userCommand: prompt,
        userId: event.userId,
        executionSteps: [],
        finalResponse: undefined,
        totalExecutionTimeMs: 0,
        status: 'inProgress',
        searchedOpinions: [],
        searchContexts: []
      };

      const updatedFlow = { ...existingFlow };

      // Update flow based on event type
      switch (event.eventType) {
        case 'ExecutionStarted':
          updatedFlow.userCommand = event.userCommand || prompt;
          updatedFlow.startTime = event.timestamp;
          updatedFlow.userId = event.userId;
          break;

        case 'OrchestratorDecision':
          updatedFlow.orchestratorDecision = event.decision;
          break;

        case 'SearchParametersGenerated':
          updatedFlow.searchContexts = [
            ...updatedFlow.searchContexts,
            {
              vectorQueryText: event.vectorQueryText,
              contraryQueryText: event.contraryQueryText,
              filters: event.filters,
              maxResults: event.maxResults,
              maxPassagesPerDocument: event.maxPassagesPerDocument,
              attemptNumber: event.attemptNumber,
              timestamp: event.timestamp
            }
          ];
          break;

        case 'SearchCompleted':
          const searchContextIndex = updatedFlow.searchContexts.findIndex(
            (ctx: any) => ctx.attemptNumber === event.attemptNumber
          );
          
          if (searchContextIndex >= 0) {
            updatedFlow.searchContexts[searchContextIndex] = {
              ...updatedFlow.searchContexts[searchContextIndex],
              parameterGenerationTimeMs: event.parameterGenerationTimeMs,
              embeddingGenerationTimeMs: event.embeddingGenerationTimeMs,
              searchExecutionTimeMs: event.searchExecutionTimeMs,
              resultCount: event.resultCount,
              embeddingTokensUsed: event.embeddingTokensUsed,
              cachedEmbeddingTokens: event.cachedEmbeddingTokens
            };
          }
          
          const existingIds = new Set(updatedFlow.searchedOpinions.map((o: any) => o.id));
          const newOpinions = event.opinions.filter((o: any) => !existingIds.has(o.id));
          updatedFlow.searchedOpinions = [...updatedFlow.searchedOpinions, ...newOpinions];
          break;

        case 'AgentStepStarted':
          updatedFlow.executionSteps = [
            ...updatedFlow.executionSteps,
            {
              startTime: event.timestamp,
              agentType: event.agentType,
              inputJson: event.inputJson,
              outputJson: '',
              executionTimeMs: 0,
              modelUsed: event.modelUsed,
              temperature: event.temperature,
              topP: event.topP,
              seed: event.seed,
              frequencyPenalty: event.frequencyPenalty,
              presencePenalty: event.presencePenalty,
              systemPromptHash: event.systemPromptHash
            }
          ];
          break;

        case 'AgentStepCompleted':
          const steps = [...updatedFlow.executionSteps];
          const lastStepIndex = steps.findIndex(
            (s: any) => s.agentType === event.agentType && !s.outputJson
          );
          if (lastStepIndex >= 0) {
            steps[lastStepIndex] = {
              ...steps[lastStepIndex],
              outputJson: event.outputJson,
              executionTimeMs: event.executionTimeMs,
              costDetail: event.costDetail,
              modelUsed: event.modelUsed,
              toolCalls: event.toolCalls
            };
            updatedFlow.executionSteps = steps;
          }
          break;

        case 'ClarificationRequested':
          updatedFlow.status = 'needsClarification';
          break;

        case 'ClarificationReceived':
          updatedFlow.status = 'inProgress';
          break;

        case 'FolioExpansionCompleted':
          updatedFlow.folioExpansion = {
            complexityScore: event.complexityScore,
            threshold: event.threshold,
            wasSkipped: event.wasSkipped,
            termCount: event.termCount,
            conceptCount: event.conceptCount,
            terms: event.terms || [],
            concepts: event.concepts || [],
            elapsedMs: event.elapsedMs,
            costDetail: event.costDetail,
            message: event.message
          };
          break;

        case 'ExecutionCompleted':
          updatedFlow.endTime = event.timestamp;
          updatedFlow.finalResponse = event.finalResponse;
          updatedFlow.totalExecutionTimeMs = event.totalExecutionTimeMs;
          updatedFlow.status = (() => {
            switch (event.status) {
              case 'success':
              case 'direct_search':
                return 'completed';
              case 'blocked':
                return 'blocked';
              case 'needs_clarification':
                return 'needsClarification';
              case 'no_data':
              case 'no_relevant_data':
              case 'no_relevant_results':
                return 'noData';
              default:
                return 'failed';
            }
          })();
          
          // Check if all provider executions are complete
          // Use the updated state that includes this completion
          const updatedFlows: Record<string, AgentExecutionFlow> = {
            ...prev,
            [eventExecutionId]: updatedFlow
          };
          
          const allComplete = test.providerConfigs.every(config => {
            // Extract base execution ID from the current event
            const baseExecutionId = eventExecutionId.substring(0, eventExecutionId.lastIndexOf('_'));
            const executionId = `${baseExecutionId}_${config.id}`;
            const flow = updatedFlows[executionId];
            const status = flow?.status;
            return status === 'completed' || status === 'failed' || status === 'blocked' || status === 'needsClarification' || status === 'noData';
          });
          
          if (allComplete) {
            setIsExecuting(false);
          }
          break;
      }

      return {
        ...prev,
        [eventExecutionId]: updatedFlow
      };
    });
  }, [test, prompt, currentExecution]);

  // Subscribe to SignalR events early (before execution starts)
  useEffect(() => {
    if (!test) return;

    signalR.onExecutionEvent(handleExecutionEvent);

    return () => {
      // Cleanup handled by onExecutionEvent replacing handlers
    };
  }, [test, handleExecutionEvent, signalR]);

  // Execute the test
  const executeTest = async () => {
    if (!test || !prompt.trim()) return;

    try {
      setIsExecuting(true);
      setError(null);
      setShowLimitWarning(false);
      setIsPromptPanelCollapsed(true); // Auto-collapse on execute

      // Generate execution ID on the client
      const executionId = crypto.randomUUID();
      setCurrentExecution(executionId);

      // Initialize execution flows for all providers immediately (UI is ready before API call)
      const initialFlows: Record<string, AgentExecutionFlow> = {};
      test.providerConfigs.forEach(config => {
        const providerExecutionId = `${executionId}_${config.id}`;
        initialFlows[providerExecutionId] = {
          executionId: providerExecutionId,
          startTime: new Date().toISOString(),
          userCommand: prompt.trim(),
          userId: '',
          executionSteps: [],
          finalResponse: undefined,
          totalExecutionTimeMs: 0,
          status: 'inProgress',
          searchedOpinions: [],
          searchContexts: []
        };
      });
      setExecutionFlows(initialFlows);
      setExecutionEvents({});

      // Execute evaluation (backend will create record and start execution)
      await evaluationApi.executeEvaluation(executionId, test.id, prompt.trim());

      // SignalR events will update the execution flows in real-time

    } catch (err) {
      console.error('Failed to execute test:', err);
      setError(err instanceof Error ? err.message : 'Failed to execute test');
      setIsExecuting(false);
      setCurrentExecution(null);
    }
  };

  // Handle favorite prompt actions
  const isPromptFavorited = cachedPrompts.some(p => p.text === prompt.trim());

  const handleToggleFavorite = () => {
    if (!prompt.trim() || test?.mode === 'Fixed') return;
    
    if (isPromptFavorited) {
      const cachedPrompt = cachedPrompts.find(p => p.text === prompt.trim());
      if (cachedPrompt) {
        removeFromCache(cachedPrompt.id);
      }
    } else {
      addToCache(prompt.trim());
    }
  };

  const handleCopyPrompt = async (promptText: string, id: string) => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div data-component="EvaluationExecutionInterface" className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading evaluation test...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error || !test) {
    return (
      <div data-component="EvaluationExecutionInterface" className="flex items-center justify-center h-screen">
        <div className="max-w-md p-6 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <h2 className="text-lg font-semibold text-red-900">Error</h2>
          </div>
          <p className="text-red-700">{error || 'Evaluation test not found'}</p>
          <button
            onClick={() => navigate('/opinions/evaluation')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to Evaluation List
          </button>
        </div>
      </div>
    );
  }

  return (
    <AppLayout
      pageTitle={test.name}
      pageDescription={test.purpose}
    >
      {/* Prompt Input Section */}
      <div data-component="EvaluationExecutionInterface" className="bg-white border-b border-gray-200 relative">
        {/* Clickable Header Bar */}
        <button
          onClick={() => setIsPromptPanelCollapsed(!isPromptPanelCollapsed)}
          className="w-full bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer relative z-0"
          title={isPromptPanelCollapsed ? "Click to expand prompt input" : "Click to collapse prompt input"}
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isPromptPanelCollapsed && prompt && (
              <span className="text-sm text-gray-700 whitespace-normal break-words" title={prompt}>
                {prompt}
              </span>
            )}
          </div>
        </button>

        {/* Floating Collapse/Expand Button - Centered and overlapping */}
        <div className="absolute left-1/2 -translate-x-1/2 -bottom-3 z-50">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsPromptPanelCollapsed(!isPromptPanelCollapsed);
            }}
            className="w-8 h-8 flex items-center justify-center bg-white border-2 border-gray-300 rounded-full shadow-lg hover:bg-gray-50 hover:shadow-xl hover:border-gray-400 transition-all"
            title={isPromptPanelCollapsed ? "Expand prompt input" : "Collapse prompt input"}
          >
            {isPromptPanelCollapsed ? (
              <ChevronDown className="w-5 h-5 text-gray-700" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-700" />
            )}
          </button>
        </div>

        {/* Collapsible Content with Animation */}
        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isPromptPanelCollapsed ? 'max-h-0 opacity-0' : 'max-h-[500px] opacity-100'
          }`}
        >
          <div className="p-4 pb-6">
            <div className="max-w-7xl mx-auto space-y-3">
              {/* Cached Prompts - Only show in Open mode */}
              {test.mode === 'Open' && cachedPrompts.length > 0 && (
                <div data-flow="cached-prompts">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="text-xs text-gray-600 font-medium">Favorites:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {cachedPrompts.map((cachedPrompt) => (
                      <div
                        key={cachedPrompt.id}
                        className="flex items-center bg-blue-50 border border-blue-200 rounded-md overflow-hidden group hover:bg-blue-100 transition-colors"
                      >
                        <button
                          type="button"
                          onClick={() => setPrompt(cachedPrompt.text)}
                          disabled={isExecuting}
                          className="px-2 py-1 text-xs text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors truncate max-w-[200px]"
                          title={cachedPrompt.text}
                        >
                          {cachedPrompt.text.length > 40 ? `${cachedPrompt.text.substring(0, 40)}...` : cachedPrompt.text}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyPrompt(cachedPrompt.text, cachedPrompt.id);
                          }}
                          disabled={isExecuting}
                          className="px-1.5 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors opacity-0 group-hover:opacity-100"
                          title={copiedId === cachedPrompt.id ? "Copied!" : "Copy"}
                        >
                          <Copy className={`w-3 h-3 ${copiedId === cachedPrompt.id ? 'text-green-500' : ''}`} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromCache(cachedPrompt.id);
                          }}
                          disabled={isExecuting}
                          className="px-1.5 py-1 text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors opacity-0 group-hover:opacity-100"
                          title="Remove"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Prompt Input Row - Combined layout */}
              <div className="flex gap-2 items-start">
                {/* Star Button - Only in Open mode */}
                {test.mode === 'Open' && (
                  <button
                    type="button"
                    onClick={handleToggleFavorite}
                    disabled={isExecuting || !prompt.trim()}
                    className={`px-2 py-2 rounded-lg transition-colors flex-shrink-0 ${
                      isPromptFavorited
                        ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={isPromptFavorited ? "Remove from favorites" : "Add to favorites"}
                  >
                    <Star className={`w-4 h-4 ${isPromptFavorited ? 'fill-current' : ''}`} />
                  </button>
                )}

                {/* Mode indicator icon */}
                <div className="flex items-center justify-center px-2 py-2 flex-shrink-0">
                  {test.mode === 'Fixed' ? (
                    <span title="Fixed prompt mode">
                      <Lock className="w-4 h-4 text-gray-500" />
                    </span>
                  ) : (
                    <span title="Open prompt mode">
                      <Unlock className="w-4 h-4 text-gray-500" />
                    </span>
                  )}
                </div>

                {/* Textarea */}
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  disabled={test.mode === 'Fixed' || isExecuting}
                  placeholder={test.mode === 'Open' ? 'Enter your prompt...' : ''}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm ${
                    test.mode === 'Fixed' 
                      ? 'bg-gray-100 text-gray-700 cursor-not-allowed' 
                      : 'bg-white text-gray-900'
                  }`}
                  rows={3}
                  data-flow={test.mode === 'Fixed' ? 'fixed-prompt' : 'open-prompt'}
                />

                {/* Execute Button with Test Counter */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={handleExecuteClick}
                    disabled={!prompt.trim() || isExecuting}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExecuting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Executing...</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4" />
                        <span className="text-sm">Execute</span>
                      </>
                    )}
                  </button>
                  
                  {/* Test Counter */}
                  {test.executionLimit && (
                    <div className="flex items-center justify-center gap-1 text-xs text-gray-600">
                      <span className="font-medium">Tests:</span>
                      <span title="Completed tests" className="cursor-help">{scoredExecutionCount}</span>
                      <span>/</span>
                      <span title="Total required tests" className="cursor-help">{test.executionLimit}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section - Side-by-side provider results */}
      {currentExecution && (
        <div data-flow="execution-results" className="flex-1 overflow-hidden">
          <div className="h-full flex">
            {test.providerConfigs.map((config) => {
              const executionId = `${currentExecution}_${config.id}`;
              const flow = executionFlows[executionId];
              const events = executionEvents[executionId] || [];
              
              // Render based on view mode
              if (viewMode === 'user') {
                return (
                  <SimplifiedProviderColumn
                    key={config.id}
                    providerName={config.name}
                    providerId={config.id}
                    executionFlow={flow}
                    isLoading={isExecuting}
                    columnWidth={`${100 / test.providerConfigs.length}%`}
                    evaluationExecutionId={currentExecution}
                    hideHeader={true}
                    executionEvents={events}
                    onSubmitClarification={signalR.submitClarification}
                  />
                );
              }
              
              return (
                <ProviderResultColumn
                  key={config.id}
                  providerName={config.name}
                  providerId={config.id}
                  executionFlow={flow}
                  isLoading={isExecuting}
                  columnWidth={`${100 / test.providerConfigs.length}%`}
                  evaluationExecutionId={currentExecution}
                  executionEvents={events}
                  onSubmitClarification={signalR.submitClarification}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Execution Limit Warning Popup */}
      {showLimitWarning && test.executionLimit && (
        <ExecutionLimitWarningPopup
          limit={test.executionLimit}
          currentCount={scoredExecutionCount}
          onContinue={executeTest}
          onCancel={() => setShowLimitWarning(false)}
        />
      )}
    </AppLayout>
  );
};
